import { useMemo, Suspense, useEffect, useState } from "react";
import Loading from "../loading/Loading";
import { VideoPlayerPlaceholderBackground } from "../Placeholder";
import styles from "components/EmbeddedVideoPlayer/index.module.scss";
import classNames from "classnames/bind";
import React from "react";
import { useHandleDefaultState } from "components/EmbeddedVideoPlayerLite/hooks/useHandleDefaultState";
import { getCorrectedAspectRatioThumbnailURL } from "helpers/imageHelpers";
import { BroadcastResponse } from "@switcherstudio/player-api-client";
import { useCollectionVideoThumbnail } from "hooks/useCollectionVideoThumbnail";
import useHandleLivePolling from "components/EmbeddedVideoPlayerLite/hooks/useHandleLivePolling";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useMediaQuery } from "hooks/useMediaQuery";
import { Image } from "components/Image";
import { AppDispatch } from "store/store";
import useHandleProcessingBroadcastPolling from "./hooks/useHandleProcessingBroadcastPolling";

const cx = classNames.bind(styles);

const ShakaPlayer = React.lazy(() => import("../ShakaPlayer"));

export interface VideoUrl {
    dash: string | undefined;
    hls: string | undefined;
}

interface LiteEmbeddedVideoPlayerProps {
    preloadedBroadcast?: BroadcastResponse | null;
}

export const EmbeddedVideoPlayerLite = ({
    preloadedBroadcast = null
}: LiteEmbeddedVideoPlayerProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { currentCollectionVideo, hasSetDefault } = useSelector(
        (s: RootState) => s.videoSession
    );
    const { hasLoadedSingleBroadcastResponse, hasLoadingError } = useSelector(
        (s: RootState) => s.catalogState
    );
    const isTablet = useMediaQuery({ maxWidth: 915, minWidth: 720 });
    const isSmallTablet = useMediaQuery({ maxWidth: 720, minWidth: 600 });

    // handle the default state initial load
    useHandleDefaultState(preloadedBroadcast);

    // begin polling for latest live data
    useHandleLivePolling();

    // Begin polling for processing data
    useHandleProcessingBroadcastPolling(preloadedBroadcast?.details?.id || "");

    // handle currentCollectionVideo switch
    const [isLiveProcessing, setIsLiveProcessing] = useState<boolean>(false);
    const [videoUrl, setVideoUrl] = useState<VideoUrl>();
    const [videoPoster, setVideoPoster] = useState<string>();
    const [resetKey, setResetKey] = useState<number>(0);

    useEffect(() => {
        if (!hasSetDefault) return;

        const video = currentCollectionVideo?.broadcast?.videos?.[0];

        // set if current video is processing but the user was watching it.
        setIsLiveProcessing(
            currentCollectionVideo?.broadcast?.details?.broadcastStatus ===
                "Ended" && video?.details?.status === "live-inprogress"
        );

        const hls = video?.details?.playback?.hls;
        const dash = video?.details?.playback?.dash;
        setVideoUrl({ hls, dash } as VideoUrl);

        setVideoPoster(
            currentCollectionVideo?.broadcast?.thumbnail?.details?.url ??
                video?.details?.thumbnail
        );

        // if the video is processing, we need to set the resetKey to force a re-render
        if (isLiveProcessing) {
            setResetKey((prevKey) => prevKey + 1);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currentCollectionVideo, hasSetDefault]);

    const isLive = useMemo(() => {
        return (
            currentCollectionVideo?.broadcast?.details?.broadcastStatus ===
            "Active"
        );
    }, [currentCollectionVideo?.broadcast?.details?.broadcastStatus]);

    const unpublished = useMemo(
        () =>
            currentCollectionVideo?.broadcast?.details?.broadcastStatus ===
            "Unpublished",
        [currentCollectionVideo?.broadcast?.details?.broadcastStatus]
    );

    const { isScheduled, scheduledThumbnail, thumbnail } =
        useCollectionVideoThumbnail({
            collectionVideo: currentCollectionVideo,
            variant: isTablet && !isSmallTablet ? "compact" : "full"
        });

    const resizedPoster = useMemo(
        () => getCorrectedAspectRatioThumbnailURL(videoPoster, 1920),
        [videoPoster]
    );

    const isLoading = useMemo(
        () => !hasLoadedSingleBroadcastResponse || !hasSetDefault,
        [hasLoadedSingleBroadcastResponse, hasSetDefault]
    );
    const displayError = useMemo(
        () => currentCollectionVideo == null && hasLoadingError,
        [currentCollectionVideo, hasLoadingError]
    );
    const isPracticePending = useMemo(
        () =>
            currentCollectionVideo?.broadcast?.details?.broadcastStatus ===
                "Ready" &&
            currentCollectionVideo?.broadcast?.details?.broadcastType ===
                "Practice",
        [currentCollectionVideo]
    );

    return (
        <div className={cx("embedded-video-player")} key={resetKey}>
            <div className={cx("video-player", "lite-player")}>
                <VideoPlayerPlaceholderBackground>
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {displayError && (
                                <div className={cx("error-message")}>
                                    There was an issue retrieving the latest
                                    video data.
                                </div>
                            )}

                            {(unpublished || !currentCollectionVideo) &&
                                !isLoading && (
                                    <div className={cx("error-message")}>
                                        This video is not available for viewing.
                                    </div>
                                )}

                            {currentCollectionVideo != null && isScheduled && (
                                <div className={cx("countdown-container")}>
                                    <Image
                                        src={thumbnail}
                                        alt="countdown thumbnail"
                                        className={cx("countdown-thumbnail")}
                                    />
                                    <div
                                        className={cx(
                                            "countdown-thumbnail",
                                            "future"
                                        )}
                                    >
                                        {scheduledThumbnail}
                                    </div>
                                </div>
                            )}

                            {isPracticePending && (
                                <div className={cx("error-message")}>
                                    Enter practice mode in the Switcher iOS app
                                    and watch your test livestream here.
                                </div>
                            )}
                        </>
                    )}
                </VideoPlayerPlaceholderBackground>

                {currentCollectionVideo != null &&
                    !unpublished &&
                    !isScheduled &&
                    !isPracticePending && (
                        <>
                            <Suspense fallback={<></>}>
                                <ShakaPlayer
                                    key={videoUrl?.dash}
                                    src={videoUrl}
                                    poster={resizedPoster}
                                    isLive={isLive}
                                    isFeaturedTrailer
                                    isLitePlayer
                                />
                            </Suspense>
                        </>
                    )}
            </div>
        </div>
    );
};
